import { Container, Graphics } from 'pixi.js';
import type { Application } from 'pixi.js';
import type { Palette } from '../palette/palettes';
import { bindCapabilitiesList } from '../ui/capabilitiesList';
import { bindContactForm, isPointerOverContactForm, updateSendMagnet } from '../ui/contactForm';
import { mountComplexThingsBranches } from '../ui/complexThingsBranches';
import { mountLinkedInSurfaceBg } from '../ui/linkedinSurfaceBg';
import { LINKEDIN_URL } from '../config/links';
import { BackPuzzle } from './BackPuzzle';
import { LinkedInOrbit } from './LinkedInOrbit';
import { createGrainOverlay, resizeGrainOverlay } from '../render/grainTexture';
import {
  activateCustomCursor,
  clearCustomCursorMagnet,
  deactivateCustomCursor,
  setCustomCursorClientPos,
  setCustomCursorInZone,
  setCustomCursorIntensity,
} from '../ui/customCursor';

type RevealPhase = 'idle' | 'hold' | 'expand' | 'open';

function materialDecel(t: number): number {
  const x = Math.min(1, Math.max(0, t));
  return 1 - Math.pow(1 - x, 3);
}

export class ContactScene {
  readonly root = new Container();
  private bg = new Graphics();
  private grain = createGrainOverlay();
  private orbit: LinkedInOrbit;
  private w = 1;
  private h = 1;
  private revealPhase: RevealPhase = 'idle';
  private revealT = 0;
  private revealOrigin = { x: 0, y: 0 };
  private readonly holdDur = 0.12;
  private expandDur = 0.48;
  private reducedMotion = false;
  private readonly panel: HTMLElement | null;
  private readonly surface: HTMLElement | null;
  private unbindForm: (() => void) | null = null;
  private unbindCapabilities: (() => void) | null = null;
  private unbindBranches: (() => void) | null = null;
  private unbindLinkedInBg: (() => void) | null = null;
  private listening = false;
  private backPuzzle: BackPuzzle;
  private linkedinOpen = false;

  constructor(
    private readonly app: Application,
    private readonly palette: Palette,
  ) {
    this.panel = document.getElementById('contact-panel');
    this.surface = document.getElementById('linkedin-surface');
    this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    this.expandDur = this.reducedMotion ? 0.18 : 0.48;
    this.orbit = new LinkedInOrbit(this.palette, {
      onComplete: (ox, oy) => this.onOrbitComplete(ox, oy),
    });
    this.root.addChild(this.bg, this.grain, this.orbit.root);
    this.app.stage.addChild(this.root);
    const backMount = document.getElementById('linkedin-back-puzzle');
    if (!backMount) throw new Error('Missing #linkedin-back-puzzle');
    this.backPuzzle = new BackPuzzle(backMount, this.palette, () => this.dismissSurface());
    this.wireSurface();
  }

  show(options?: { deferText?: boolean }): void {
    this.linkedinOpen = false;
    this.resetReveal();
    this.orbit.root.visible = true;
    this.draw();
    if (this.panel) {
      this.panel.hidden = false;
      this.panel.classList.remove('is-text-reveal');
      if (options?.deferText) {
        this.panel.classList.add('is-text-pending');
      } else {
        this.panel.classList.remove('is-text-pending');
        this.beginTextReveal();
      }
      this.unbindForm?.();
      this.unbindForm = bindContactForm(this.panel.querySelector('#contact-form'));
      this.unbindCapabilities?.();
      this.unbindCapabilities = bindCapabilitiesList(
        this.panel.querySelector('.contact-capabilities'),
      );
      document.body.style.overflow = 'hidden';
      const canvas = this.app.canvas;
      canvas.style.pointerEvents = 'none';
      canvas.style.touchAction = 'auto';
    }
    this.bindPointer();
    activateCustomCursor();
  }

  /** Called after square-burst transition completes. */
  finishTransitionShow(): void {
    if (this.panel) {
      this.panel.hidden = false;
      document.body.style.overflow = 'hidden';
    }
    this.bindPointer();
    activateCustomCursor();
  }

  beginTextReveal(): void {
    if (!this.panel) return;
    this.panel.classList.remove('is-text-pending');
    this.panel.classList.add('is-text-reveal');

    const items = this.panel.querySelectorAll<HTMLElement>('.contact-capabilities li');
    items.forEach((li, i) => {
      li.style.setProperty('--reveal-i', String(i));
    });

    const blocks = this.panel.querySelectorAll<HTMLElement>(
      '.contact-copy > *, .contact-form > *, .contact-footer-links',
    );
    blocks.forEach((el, i) => {
      el.style.setProperty('--reveal-i', String(i + items.length));
    });

    this.unbindBranches?.();
    requestAnimationFrame(() => {
      if (!this.panel?.classList.contains('is-text-reveal')) return;
      this.unbindBranches = mountComplexThingsBranches(
        this.panel.querySelector('.complex-headline'),
        { reducedMotion: this.reducedMotion, panel: this.panel },
      );
    });
  }

  hide(): void {
    this.unbindPointer();
    this.resetReveal();
    if (this.panel) {
      this.panel.hidden = true;
      this.panel.classList.remove('is-text-pending', 'is-text-reveal');
    }
    this.unbindForm?.();
    this.unbindForm = null;
    this.unbindCapabilities?.();
    this.unbindCapabilities = null;
    this.unbindBranches?.();
    this.unbindBranches = null;
    this.unbindLinkedInBg?.();
    this.unbindLinkedInBg = null;
    document.body.style.overflow = 'hidden';
    const canvas = this.app.canvas;
    canvas.style.pointerEvents = 'auto';
    canvas.style.touchAction = 'none';
    deactivateCustomCursor();
  }

  resize(w: number, h: number): void {
    this.w = w;
    this.h = h;
    this.backPuzzle.resize(w, h);
    if (this.revealPhase === 'open' || this.revealPhase === 'expand') {
      this.draw();
      this.applyClip(this.revealPhase === 'open' ? 150 : undefined);
      return;
    }
    this.draw();
  }

  update(dt: number): void {
    if (this.revealPhase === 'idle' && !this.linkedinOpen) {
      this.orbit.update(dt);
    }

    if (this.revealPhase === 'hold') {
      this.revealT += dt;
      if (this.revealT >= this.holdDur) {
        this.revealPhase = 'expand';
        this.revealT = 0;
        this.beginCircularReveal();
      }
      return;
    }

    if (this.revealPhase === 'expand') {
      this.revealT += dt;
      const t = Math.min(1, this.revealT / this.expandDur);
      const e = materialDecel(t);
      const r = 2 + e * 148;
      this.applyClip(r);
      if (t >= 1) {
        this.revealPhase = 'open';
        this.applyClip(150);
        this.surface?.classList.remove('is-revealing');
        this.surface?.classList.add('is-open');
        this.linkedinOpen = true;
        this.openBackPuzzle();
        document.getElementById('linkedin-continue')?.focus();
      }
      return;
    }

    if (this.revealPhase === 'open') {
      this.backPuzzle.update(dt);
    }
  }

  destroy(): void {
    this.hide();
    this.backPuzzle.destroy();
    this.root.destroy({ children: true });
  }

  private wireSurface(): void {
    const link = document.getElementById('linkedin-continue') as HTMLAnchorElement | null;
    if (link) link.href = LINKEDIN_URL;
  }

  private resetReveal(): void {
    this.revealPhase = 'idle';
    this.revealT = 0;
    this.linkedinOpen = false;
    this.orbit.reset();
    this.closeBackPuzzle();
    this.unbindLinkedInBg?.();
    this.unbindLinkedInBg = null;
    if (this.surface) {
      this.surface.hidden = true;
      this.surface.classList.remove('is-revealing', 'is-open', 'is-closing');
      this.surface.style.removeProperty('--reveal-r');
      this.surface.style.removeProperty('--reveal-x');
      this.surface.style.removeProperty('--reveal-y');
    }
  }

  private onOrbitComplete(ox: number, oy: number): void {
    if (this.revealPhase !== 'idle') return;
    this.revealOrigin = { x: ox, y: oy };
    this.revealPhase = 'hold';
    this.revealT = 0;
    deactivateCustomCursor();
  }

  private dismissSurface(): void {
    if (!this.surface || this.surface.classList.contains('is-closing')) return;
    this.closeBackPuzzle();
    this.surface.classList.add('is-closing');
    const finish = (): void => {
      this.surface?.removeEventListener('transitionend', onEnd);
      if (!this.surface?.classList.contains('is-closing')) return;
      this.finishDismiss();
    };
    const onEnd = (e: TransitionEvent): void => {
      if (e.target !== this.surface || e.propertyName !== 'clip-path') return;
      finish();
    };
    this.surface.addEventListener('transitionend', onEnd);
    window.setTimeout(finish, 650);
  }

  private finishDismiss(): void {
    this.linkedinOpen = false;
    this.resetReveal();
    this.orbit.root.visible = true;
    if (this.panel) this.panel.hidden = false;
    this.draw();
    this.bindPointer();
    activateCustomCursor();
  }

  private openBackPuzzle(): void {
    this.backPuzzle.show();
  }

  private closeBackPuzzle(): void {
    this.backPuzzle.hide();
  }

  private draw(): void {
    this.bg.clear();
    this.bg.rect(0, 0, this.w, this.h).fill(this.palette.fieldB);
    resizeGrainOverlay(this.grain, this.w, this.h);
    this.orbit.root.visible = true;
    this.orbit.layout(this.w, this.h);
  }

  private bindPointer(): void {
    if (this.listening) return;
    this.listening = true;
    window.addEventListener('pointermove', this.onPointerMove, { passive: true });
    window.addEventListener('pointerdown', this.onPointerDown, { passive: false });
    window.addEventListener('pointerup', this.onPointerUp);
    window.addEventListener('pointercancel', this.onPointerUp);
    window.addEventListener('pointerleave', this.onPointerLeave);
  }

  private unbindPointer(): void {
    if (!this.listening) return;
    this.listening = false;
    window.removeEventListener('pointermove', this.onPointerMove);
    window.removeEventListener('pointerdown', this.onPointerDown);
    window.removeEventListener('pointerup', this.onPointerUp);
    window.removeEventListener('pointercancel', this.onPointerUp);
    window.removeEventListener('pointerleave', this.onPointerLeave);
    this.orbit.setPointer(0, 0, false);
    setCustomCursorInZone(false);
    setCustomCursorClientPos(0, 0, { touch: true, pressed: false });
  }

  private onPointerLeave = (): void => {
    this.orbit.setPointer(0, 0, false);
    setCustomCursorInZone(false);
    setCustomCursorClientPos(0, 0, { touch: true, pressed: false });
  };

  private isOrbitBlocked(e: PointerEvent): boolean {
    const t = e.target as HTMLElement | null;
    return !!t?.closest?.(
      'input, textarea, button, .g-recaptcha, label, .contact-form, .contact-capabilities, .linkedin-surface, .linkedin-back-puzzle',
    );
  }

  private mapOrbitPointer(e: PointerEvent, active: boolean): void {
    const rect = this.app.canvas.getBoundingClientRect();
    const x = ((e.clientX - rect.left) / rect.width) * this.w;
    const y = ((e.clientY - rect.top) / rect.height) * this.h;
    const isTouch = e.pointerType === 'touch';
    const inOrbitZone = this.orbit.containsPoint(x, y);

    if (isPointerOverContactForm(e.clientX, e.clientY)) {
      this.orbit.setPointer(0, 0, false);
      setCustomCursorInZone(false);
      updateSendMagnet(e.clientX, e.clientY);
      setCustomCursorClientPos(e.clientX, e.clientY, { touch: isTouch, pressed: active && isTouch });
      return;
    }

    clearCustomCursorMagnet();

    if (this.isOrbitBlocked(e) && !inOrbitZone) {
      this.orbit.setPointer(0, 0, false);
      setCustomCursorInZone(false);
      if (isTouch) setCustomCursorClientPos(e.clientX, e.clientY, { touch: true, pressed: false });
      return;
    }

    // Hover over the hub counts — no click required for the vacuum.
    this.orbit.setPointer(x, y, isTouch ? active : true);
    setCustomCursorInZone(this.orbit.pointerInZone);
    setCustomCursorClientPos(e.clientX, e.clientY, { touch: isTouch, pressed: active && isTouch });
    setCustomCursorIntensity(
      this.orbit.pointerOverHub ? 0.85 : this.orbit.pointerInZone ? 0.45 : 0.15,
      active,
      isTouch,
    );
  }

  private onPointerDown = (e: PointerEvent): void => {
    if (!this.panel || this.panel.hidden || this.linkedinOpen) return;
    if (this.revealPhase !== 'idle') return;
    if (this.isOrbitBlocked(e)) return;

    const rect = this.app.canvas.getBoundingClientRect();
    const x = ((e.clientX - rect.left) / rect.width) * this.w;
    const y = ((e.clientY - rect.top) / rect.height) * this.h;

    this.mapOrbitPointer(e, true);

    if (this.orbit.pointerInZone && !this.orbit.pointerOverHub && e.button === 0) {
      this.orbit.spawnAt(x, y);
    }

    if (e.pointerType === 'touch') e.preventDefault();
  };

  private onPointerUp = (e: PointerEvent): void => {
    if (!this.panel || this.panel.hidden || this.linkedinOpen) return;
    if (this.revealPhase !== 'idle') return;
    if (e.pointerType === 'touch') {
      this.orbit.setPointer(0, 0, false);
      setCustomCursorInZone(false);
      setCustomCursorClientPos(e.clientX, e.clientY, { touch: true, pressed: false });
      setCustomCursorIntensity(0, false, true);
      return;
    }
    this.mapOrbitPointer(e, false);
  };

  private onPointerMove = (e: PointerEvent): void => {
    if (!this.panel || this.panel.hidden || this.linkedinOpen) return;
    if (this.revealPhase !== 'idle') return;

    const isTouch = e.pointerType === 'touch';
    const active = isTouch ? e.buttons !== 0 : true;
    if (isTouch && !active) return;

    this.mapOrbitPointer(e, active);
  };

  private beginCircularReveal(): void {
    const surface = this.surface;
    if (!surface) return;

    surface.style.setProperty('--linkedin-surface', '#0a66c2');
    const xPct = (this.revealOrigin.x / Math.max(1, this.w)) * 100;
    const yPct = (this.revealOrigin.y / Math.max(1, this.h)) * 100;
    surface.style.setProperty('--reveal-x', `${xPct}%`);
    surface.style.setProperty('--reveal-y', `${yPct}%`);
    this.applyClip(2);
    surface.hidden = false;
    surface.classList.add('is-revealing');
    surface.classList.remove('is-open', 'is-closing');
    if (this.panel) this.panel.hidden = true;

    this.unbindLinkedInBg?.();
    requestAnimationFrame(() => {
      this.unbindLinkedInBg = mountLinkedInSurfaceBg(surface, {
        reducedMotion: this.reducedMotion,
        originXPct: this.revealOrigin.x / Math.max(1, this.w),
        originYPct: this.revealOrigin.y / Math.max(1, this.h),
      });
    });
  }

  private applyClip(radiusPct?: number): void {
    if (radiusPct !== undefined) {
      this.surface?.style.setProperty('--reveal-r', `${radiusPct}%`);
    }
  }
}
